import React, { useContext, useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import Cookies from 'js-cookie';

import { toast } from 'react-toastify';

import {
	Flex,
	Modal,
	Checkbox,
	Button,
	Tooltip
} from 'antd';
import {
	EditOutlined,
	QuestionCircleOutlined
} from '@ant-design/icons';
import {
	usePatchObserverMutation,
	useLazyGetObserverByIdQuery,
	useLazyGetObserverWhatsappGroupByIdQuery,
	useDeleteObserverWhatsappGroupMutation,
	usePostObserverWhatsappGroupMutation,
	useForceObserverExecutionMutation
} from 'src/redux/services/centralApi';
import {
	OBSERVER_REPORT_SOURCE,
	type Observer,
	type IQueryFieldsItem,
	type RowRestriction,
	type FieldRestriction,
	type ICondition,
	type ReportBlock
} from 'src/interfaces/entities/observer';
import {
	CodeEditor,
	PeriodicitySelect,
	PreviewMessageInput,
	RestrictionsArea,
	SeveritySelect,
	TagsSelect,
	WhatsAppGroupsSelect,
	SqlFilter,
	FormItemInput,
	QueryDisplay,
	ReportBlocksEditor
} from 'src/pages/observers/components';
import type { IQueryResult } from 'src/pages/observers/components/CodeEditor/types';
import ModuleSelect from 'src/pages/observers/components/ModuleSelect';
import { checkRole } from 'src/common/helpers/checkRole';
import { Field } from 'react-querybuilder';
import { AuthContext } from 'src/AuthProvider';

export default function EditObserver(
	{
		row,
	}: { row: Observer }
) {
	const { user, } = useContext(AuthContext);

	const [ forceExecution, forceExecutionResult, ] = useForceObserverExecutionMutation();

	const [
		getObserverWhatsappGroupData,
		observerWhatsappGroupDataResult,
	] = useLazyGetObserverWhatsappGroupByIdQuery();
	const [
		getObserverData,
		observerDataResult,
	] = useLazyGetObserverByIdQuery();

	const [
		patchObserver,
		patchObserverResult,
	] = usePatchObserverMutation();

	const [ postObserverWhatsappGroup, ] = usePostObserverWhatsappGroupMutation();
	const [ deleteObserverWhatsappGroup, ] = useDeleteObserverWhatsappGroupMutation();

	const [ open, setOpen, ] = useState<boolean>(false);

	const [ name, setName, ] = useState<string>(row.name);
	const [ tags, setTags, ] = useState<string[]>(row.tags);
	const [ isActive, setIsActive, ] = useState<boolean>(row.is_active);
	const [ severity, setSeverity, ] = useState<string>(row.severity_id ?? '');
	const [ shortName, setShortName, ] = useState<string>(row.short_name);
	const [ module, setModule, ] = useState<string | null>(row.module_id);
	const [ periodicity, setPeriodicity, ] = useState<string>(row.periodicity_id);
	const [ customPreviewMessage, setCustomPreviewMessage, ] = useState<string>(row.custom_preview_message);
	const [ onlyNotify, setOnlyNotify, ] = useState<boolean>(Array.isArray(row.flags) ? row.flags.includes('only_notify') : false);
	const [ sendSqlResult, setSendSqlResult, ] = useState<boolean>(Array.isArray(row.flags) ? row.flags.includes('send_sql_result') : false);
	const [ reportBlocks, setReportBlocks, ] = useState<ReportBlock[]>(row.report?.blocks ?? []);
	const [ whatsappGroups, setWhatsappGroups, ] = useState<string[]>([]);
	const [ rowRestrictions, setRowRestrictions, ] = useState<RowRestriction[]>([]);
	const [ fieldRestrictions, setFieldRestrictions, ] = useState<FieldRestriction[]>([]);

	const [ defaultWhatsappGroups, setDefaultWhatsappGroups, ] = useState<string[]>([]);

	const [ conditions, setConditions, ] = useState<Record<string, ICondition>>(row.conditions ?? {});

	const [ queryFields, setQueryFields, ] = useState<IQueryFieldsItem[]>(
		Array.isArray(row.sql_test_result) ? row.sql_test_result : []
	);

	/** SQL text atualizado pelo editor (somente para partner+) */
	const [ sqlCode, setSqlCode, ] = useState<string>('');
	/** QueryResult do teste de SQL (somente para partner+) */
	const [ queryResult, setQueryResult, ] = useState<IQueryResult | undefined>(undefined);
	const previewData = useMemo(() => ({ [OBSERVER_REPORT_SOURCE]: queryResult?.data ?? [], }), [ queryResult, ]);

	const isSuperUser = useMemo(() => {
		return checkRole('super_admin', { user, });
	}, [ user, ]);

	/** true para partner e super_admin */
	const isPartnerOrAbove = useMemo(() => {
		return checkRole('partner', { user, }) ?? false;
	}, [ user, ]);

	/**
	 * true quando um parceiro/super_admin está impersonando uma organização:
	 * o role ativo é 'admin' (JWT da org alvo), mas o super_token original ainda existe.
	 */
	const isImpersonating = !isPartnerOrAbove && !!Cookies.get('intraguard.super_token');

	/**
	 * Permite editar o SQL se:
	 * - Parceiro+ sem SQL de módulo (comportamento original), OU
	 * - Parceiro impersonando uma organização (pode editar inclusive SQL de módulo)
	 */
	const canEditSql = (isPartnerOrAbove && !row.module_sql_url) || isImpersonating;

	useEffect(() => {
		if (open) {
			const id = row.observer_id;
			getObserverData({ id, });
			getObserverWhatsappGroupData({ id, });
		}
	}, [ open, row, ]);

	useEffect(() => {
		if (!observerWhatsappGroupDataResult.data) return;
		const myGroupsStringArray = observerWhatsappGroupDataResult.data
			.map(group => (
				group.group_id
			));

		setDefaultWhatsappGroups(myGroupsStringArray);
		setWhatsappGroups(myGroupsStringArray);
	}, [ observerWhatsappGroupDataResult, ]);

	useEffect(() => {
		handleResetFields();
	}, [ row, ]);

	const handleResetFields = () => {
		setName(row.name);
		setTags(row.tags);
		setIsActive(row.is_active);
		setSeverity(row.severity_id ?? '');
		setShortName(row.short_name);
		setReportBlocks(row.report?.blocks ?? []);
		setModule(row.module_id);
		setPeriodicity(row.periodicity_id);
		setCustomPreviewMessage(row.custom_preview_message);
		setConditions(row.conditions ?? {});
		setQueryFields(Array.isArray(row.sql_test_result) ? row.sql_test_result : []);
		setSqlCode('');
		setQueryResult(undefined);
		setFieldRestrictions([]);
		setRowRestrictions([]);
		if (Array.isArray(row.flags)) {
			setOnlyNotify(row.flags.includes('only_notify'));
			setSendSqlResult(row.flags.includes('send_sql_result'));
		} else {
			setOnlyNotify(false);
			setSendSqlResult(false);
		}
	};

	const handleEditObserver = () => {
		const hasRequiredData = queryFields.length > 0 &&
			name && shortName && periodicity && severity &&
			(fieldRestrictions.length > 0 || rowRestrictions.length > 0);

		if (!hasRequiredData) {
			toast.error(
				'Preencha todos os campos marcados'
				+ ' com \'*\' para avançar.'
			);
			return;
		}

		const isEditingStoreSql = canEditSql && !!sqlCode && !!row.module_sql_url;

		if (isEditingStoreSql) {
			Modal.confirm({
				title: 'Atenção: SQL compartilhado da Store',
				content:
					'Você está editando o SQL importado da Store. '
					+ 'Essa alteração afetará TODAS as organizações que utilizam '
					+ 'este mesmo SQL importado. Deseja continuar?',
				okText: 'Sim, alterar para todos',
				cancelText: 'Cancelar',
				okButtonProps: { danger: true, },
				centered: true,
				onOk: () => submitEditObserver(),
			});
		} else {
			submitEditObserver();
		}
	};

	const submitEditObserver = () => {
		const flags: string[] = [];
		if (onlyNotify) {
			flags.push('only_notify');
		}
		if (sendSqlResult) {
			flags.push('send_sql_result');
		}

		patchObserver({
			id: row.observer_id,
			observer: {
				name,
				tags,
				module_id: module,
				short_name: shortName,
				sql_url: row.sql_url,
				// Campo virtual: texto do SQL editado → API faz upload e substitui o arquivo
				sql: (canEditSql && sqlCode) ? sqlCode : undefined,
				sql_test_result: canEditSql ? queryFields : row.sql_test_result,
				custom_preview_message: customPreviewMessage,
				periodicity_id: periodicity,
				severity_id: severity,
				is_active: isActive,
				row_restrictions: rowRestrictions,
				field_restrictions: fieldRestrictions,
				module_sql_url: row.module_sql_url,
				conditions,
				flags,
				report: sendSqlResult && reportBlocks.length ? { blocks: reportBlocks, } : null,
			},
		})
			.unwrap()
			.then(() => {
				observerWhatsappGroupDataResult.data?.forEach(group => {
					if (!whatsappGroups.some(el => el === group.group_id)) {
						deleteObserverWhatsappGroup({
							observer_id: row.observer_id,
							group_id: group.group_id,
						});
					}
				});

				whatsappGroups.forEach(group => {
					const alreadyExist = observerWhatsappGroupDataResult.data
						?.some(el => el.group_id === group);

					if (!alreadyExist) {
						postObserverWhatsappGroup({
							observer_id: row.observer_id,
							group_id: group,
						});
					}
				});

				toast.success('Observador atualizado com sucesso!', {
					autoClose: 1000,
				});
			})
			.catch((err) => {
				const message = err?.data?.frontend_message
					?? err?.data?.[0]?.msg
					?? 'Erro ao atualizar o observador.';
				toast.error(message, {
					autoClose: 2000,
				});
			})
			.finally(() => {
				handleResetFields();
				setOpen(false);
			});
	};

	return <>
		<Tooltip title="Editar">
			<Button
				type="text"
				icon={<EditOutlined />}
				onClick={() => setOpen(true)}
			/>
		</Tooltip>
		<Modal
			width={700}
			centered
			title={'Editar Observador'}
			open={open}
			onCancel={() => {
				handleResetFields();
				setOpen(false);
				toast.info('Operação cancelada!', { autoClose: 1000, });
			}}
			destroyOnHidden
			confirmLoading={patchObserverResult.isLoading}
			okText="Salvar"
			onOk={() => {
				handleEditObserver();
			}}
			footer={(_, { OkBtn, CancelBtn, }) => (
				<>
					<Button
						loading={forceExecutionResult.isLoading}
						onClick={() => {
							forceExecution(row.observer_id)
								.unwrap()
								.then(({ payload, }) => {
									if(payload.execution_ok === true){
										toast.success('Observador executado! Sem ocorrência!', {
											autoClose: 1000,
										});
									} else {
										toast.success('Observador executado! Gerou ocorrência!', {
											autoClose: 1000,
										});
									}

								})
								.catch((err) => {
									if (err?.status && err?.status === 500
										&& err?.data?.frontend_message) {
										toast.error(err?.data?.frontend_message, {
											autoClose: 1000,
										});
									}
								});
						}}
					>
						Forçar execução
					</Button>
					<CancelBtn />
					<OkBtn />
				</>
			)}
		>
			<Flex vertical gap={16}>
				<div>
					{canEditSql ? (
						<>
							<CodeEditor
								conditions={conditions}
								executable
								autoExecuteMs={5000}
								hasReturn={Boolean(queryResult)}
								height='300px'
								editor_props={{
									value: observerDataResult.data?.sql || observerDataResult.data?.module_sql || '',
								}}
								onCodeChange={(value) => setSqlCode(value)}
								onQueryResultChange={(value) => setQueryResult(value)}
								onQueryFieldsChange={(value) => {
									const fieldsChanged = !_.isEqual(
										_.sortBy(value, 'field'),
										_.sortBy(queryFields, 'field')
									);
									if (fieldsChanged) {
										setFieldRestrictions([]);
										setRowRestrictions([]);
									}
									setQueryFields(value);
								}}
							/>
							{queryResult && (
								<QueryDisplay
									queryResult={queryResult}
									queryFields={queryFields}
								/>
							)}
						</>
					) : (
						<CodeEditor
							conditions={conditions}
							readOnly
							growWithCode
							editor_props={{
								value: observerDataResult.data?.module_sql || observerDataResult.data?.sql || '',
							}}
						/>
					)}
				</div>
				{
					Object.keys(conditions).map((conditionName, i) => {
						const condition = conditions[conditionName];

						const handleFilterChange = (
							updatedValue: string | number | boolean | null
						) => {
							const updatedFilter = {
								...condition,
								value: updatedValue,
							};

							setConditions(prev => ({
								...prev,
								[conditionName]: updatedFilter,
							}));
						};

						if (condition.type === 'sql' && condition.columns) {
							const fields: Field[] = [];
							condition.columns?.forEach(col =>
								fields.push(
									{
										label: col.name,
										name: col.name,
										inputType: col.type,
									}
								)
							);

							return <SqlFilter
								required={condition.required}
								key={i}
								fields={fields}
								label={`${conditionName}:`}
								onConditionChange={(value) =>
									handleFilterChange(value)
								}
							/>;
						}

						return <FormItemInput
							key={i}
							required={condition.required}
							value={condition.value}
							tooltip='Filtros dinâmicos para serem inseridos como condição na consulta SQL.'
							label={conditionName}
							placeholder={conditionName}
							inputType={condition.type}
							onInputChange={(value) => {
								handleFilterChange(value);
							}}
						/>;
					})
				}
				<PreviewMessageInput
					defaultMessage={row.custom_preview_message}
					queryFields={queryFields}
					onMessageChange={(value) =>
						setCustomPreviewMessage(value)
					}
				/>
				<FormItemInput
					value={name}
					required
					label='Nome:'
					placeholder='Nome do observador'
					onInputChange={(value) => {
						if (typeof value === 'string')
							setName(value as string);
					}}
				/>
				<FormItemInput
					readonly
					value={row.short_name}
					required
					label='Nome abreviado/simplificado:'
					placeholder='Nome do simplificado'
					onInputChange={(value) => {
						if (typeof value === 'string')
							setShortName(value);
					}}
					tooltip='Nome para ser utilizado como variável do sistema durante a criação de dashboards personalizados. Para que o desenvolvimento de dashboards seja simplificado.'
				/>
				<TagsSelect
					defaultTags={row.tags}
					onTagsChange={(value) => setTags(value)}
				/>
				<PeriodicitySelect
					defaultPeriodicity={row.periodicity_id}
					onPeriodicityChange={(value) =>
						setPeriodicity(value)
					}
				/>
				<SeveritySelect
					defaultSeverity={row.severity_id ?? undefined}
					onSeverityChange={(value) =>
						setSeverity(value)
					}
				/>
				<WhatsAppGroupsSelect
					defaultWhatsappGroup={defaultWhatsappGroups}
					onWhatsappGroupChange={(value) =>
						setWhatsappGroups(value)
					}
				/>
				{
					isSuperUser &&
					<ModuleSelect
						defaultModule={module}
						onModuleChange={(value) =>
							setModule(value)
						}
					/>
				}
				<hr />
				<Checkbox
					checked={isActive}
					onChange={(e) =>
						setIsActive(e.target.checked)
					}
				>
					Observador ativo
				</Checkbox>
				<Checkbox
					checked={onlyNotify}
					onChange={(e) =>
						setOnlyNotify(e.target.checked)
					}
				>
					Enviar notificação sem gerar ocorrência?
				</Checkbox>
				<Checkbox
					checked={sendSqlResult}
					onChange={(e) => {
						setSendSqlResult(e.target.checked);
						if (!e.target.checked) setReportBlocks([]);
					}}
				>
					Enviar relatório em anexo?{' '}
					<Tooltip title='Sem blocos, envia a tabela completa do resultado. Com blocos, envia os gráficos e tabelas escolhidos, com as linhas que dispararam o alerta.'>
						<QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help', }} />
					</Tooltip>
				</Checkbox>
				{
					sendSqlResult && <ReportBlocksEditor
						sources={[{ key: OBSERVER_REPORT_SOURCE, fields: queryFields, },]}
						blocks={reportBlocks}
						onBlocksChange={setReportBlocks}
						preview={{ title: name || 'Observador', data: previewData, }}
					/>
				}
				<RestrictionsArea
					defaultFieldRestrictions={row.field_restrictions}
					defaultRowRestrictions={row.row_restrictions}
					queryFields={queryFields}
					onRestrictionsChange={(fields, rows) => {
						setFieldRestrictions(fields);
						setRowRestrictions(rows);
					}}
				/>
			</Flex>
		</Modal >
	</>;
}